import { SupabaseClient, createClient } from '@supabase/supabase-js'
import { ReactNode, createContext, useContext, useState } from 'react'
import { Database } from 'supabase/database.types'

const SupabaseClientContext = createContext<SupabaseClient<Database> | null>(
  null
)

// Routes that show a single request by public id. RLS lets a signed-in user
// who is not a member of the owning org read that request's data only when
// the request carries this header (see can_access_quote() in the database).
const REQUEST_ROUTE_PATTERN =
  /^(?:\/app)?\/(?:share-order|requests|orders|history|guided-assistant)\/([A-Za-z0-9_-]{1,32})(?:\/|$)/

export const SHARED_QUOTE_HEADER = 'x-quote-public-id'

export const getSharedQuotePublicIdFromPath = (pathname: string) =>
  pathname.match(REQUEST_ROUTE_PATTERN)?.[1] ?? null

const fetchWithSharedQuoteHeader: typeof fetch = (input, init) => {
  const publicId =
    typeof window === 'undefined'
      ? null
      : getSharedQuotePublicIdFromPath(window.location.pathname)
  if (!publicId) return fetch(input, init)

  const headers = new Headers(init?.headers)
  headers.set(SHARED_QUOTE_HEADER, publicId)
  return fetch(input, { ...init, headers })
}

export function SupabaseProvider(props: { children: ReactNode }) {
  const [client] = useState(() =>
    createClient<Database>(
      import.meta.env.VITE_SUPABASE_URL!,
      import.meta.env.VITE_SUPABASE_ANON_KEY!,
      { global: { fetch: fetchWithSharedQuoteHeader } }
    )
  )

  return (
    <SupabaseClientContext.Provider value={client}>
      {props.children}
    </SupabaseClientContext.Provider>
  )
}

export function useSupabaseClient() {
  const client = useContext(SupabaseClientContext)

  if (client === null) {
    throw new Error(
      'Supabase client not provided via context.\n' +
        'Did you forget to wrap your component tree with SupabaseProvider?'
    )
  }

  return client
}
